import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure } from '@/pages/ibama/OverviewPage';
import { fetchIbamaInstruments } from '@/services/ibama';
import type { IbamaTrack } from '@/services/types';

/**
 * IBAMA's three instrument tracks: seizure, suspension and embargo.
 *
 * Each is a register of documents an inspector lavrou -- a stage of an
 * inspection, never a register of barred parties -- counted by year,
 * status and state and linked to the autos master where the ETL could.
 * Embargo has no head table in the source, so it is counted in items and
 * hectares. No term is ever listed: most name a natural person.
 */
export function IbamaInstrumentsPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'instruments'],
    queryFn: fetchIbamaInstruments,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const decimal = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:instruments.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:instruments.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:instruments.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path="/api/v1/ibama/export/instruments"
          label={t('ibama:instruments.export')}
        />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <FieldGuide i18nKey="ibama:instruments.guide" level={2} />

      <TrackSection track="seizure" data={data.seizure} />
      <TrackSection track="suspension" data={data.suspension} />

      <section className="rounded-lg border bg-card p-4" data-track="embargo">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:instruments.embargo.title')}
        </h2>
        <Answer answer={data.embargo} help={t('ibama:instruments.embargo.help')}>
          {(e) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('ibama:instruments.embargo.items')}
                  value={number.format(e.items)}
                />
                <Figure
                  label={t('ibama:instruments.embargo.terms')}
                  value={`${number.format(e.terms)} (${decimal.format(e.items_per_term)})`}
                />
                <Figure
                  label={t('ibama:instruments.embargo.area')}
                  value={`${number.format(Math.round(e.area_ha_total))} ha / ${number.format(
                    e.items_with_area,
                  )}`}
                />
                <Figure
                  label={t('ibama:instruments.embargo.deterProdes')}
                  value={number.format(e.items_with_deter_prodes)}
                />
              </div>
              <ul className="grid gap-1 text-sm sm:grid-cols-2">
                {e.natures.map((n) => (
                  <li key={n.code} className="flex items-baseline gap-2" data-nature={n.code}>
                    <span className="min-w-0 flex-1 truncate text-foreground" title={n.code}>
                      {n.code}
                    </span>
                    <span className="tabular-nums text-foreground">{number.format(n.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Answer>
      </section>
    </div>
  );
}

function TrackSection({ track, data }: { track: 'seizure' | 'suspension'; data: IbamaTrack }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  return (
    <section className="space-y-3" data-track={track}>
      <h2 className="text-sm font-semibold text-foreground">
        {t(`ibama:instruments.tracks.${track}.title`)}
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-shape>
          <h3 className="mb-3 text-sm font-medium text-foreground">
            {t('ibama:instruments.shape.title')}
          </h3>
          <Answer answer={data.shape} help={t(`ibama:instruments.tracks.${track}.help`)}>
            {(s) => (
              <div className="grid grid-cols-2 gap-3">
                <Figure label={t('ibama:instruments.shape.terms')} value={number.format(s.terms)} />
                <Figure
                  label={t('ibama:instruments.shape.linked')}
                  value={`${number.format(s.linked_to_auto)} (${percent.format(s.linked_share)})`}
                />
                <Figure
                  label={t('ibama:instruments.shape.companies')}
                  value={number.format(s.companies)}
                />
                <Figure
                  label={t('ibama:instruments.shape.persons')}
                  value={number.format(s.persons)}
                />
              </div>
            )}
          </Answer>
        </section>
        <OutcomeMixPanel
          answer={{ ...data.shape, value: data.shape.value?.statuses ?? null }}
          titleKey="ibama:instruments.statuses.title"
          helpKey="ibama:instruments.statuses.help"
          labelPrefix="ibama:instruments.statusCodes."
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-by-year>
          <h3 className="mb-3 text-sm font-medium text-foreground">
            {t('ibama:instruments.years.title')}
          </h3>
          <Answer answer={data.by_year} help={t('ibama:instruments.years.help')}>
            {(rows) => (
              <div className="max-h-[24rem] overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ibama:instruments.years.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:instruments.years.terms')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:instruments.years.cancelled')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:instruments.years.linked')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 tabular-nums text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(y.terms)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.cancelled)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.linked_to_auto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>
        <section className="rounded-lg border bg-card p-4" data-by-state>
          <h3 className="mb-3 text-sm font-medium text-foreground">
            {t('ibama:instruments.states.title')}
          </h3>
          <Answer answer={data.by_state} help={t('ibama:instruments.states.help')}>
            {(rows) => (
              <ul className="grid grid-cols-3 gap-1 text-sm sm:grid-cols-4">
                {rows.map((u) => (
                  <li
                    key={u.uf}
                    className="flex justify-between rounded-md border bg-muted/30 px-2 py-1"
                    data-uf={u.uf}
                  >
                    <span className="text-foreground">{u.uf}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {number.format(u.terms)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Answer>
        </section>
      </div>
    </section>
  );
}
